import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DownloadSimple, Printer, MagnifyingGlass } from "@phosphor-icons/react";
import React from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { useProcesses } from "../App";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const INSPECTED_OPS = new Set(["incoming_store", "semi_firewall", "firewall", "dispatch"]);

export default function FinalReport() {
  // Operations list comes from per-tenant Customization → Processes (live).
  // Reports show ONLY In-house processes; Outsource processes are hidden
  // per the Process Master's `is_inhouse` / `is_outsource` flags.
  const { inhouseOps: ALL_OPERATIONS, opNames: OPERATION_NAMES } = useProcesses();

  const [reportData, setReportData] = useState([]);
  const [qualityData, setQualityData] = useState(null);
  const [qualityChartData, setQualityChartData] = useState(null);
  const [qualityView, setQualityView] = useState("table"); // table | graphs
  const [scrapData, setScrapData] = useState(null);
  const [reworkData, setReworkData] = useState(null);
  const [scrapRegData, setScrapRegData] = useState(null);
  const [opWiseData, setOpWiseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("production");
  const [valueRateType, setValueRateType] = useState("part_rate");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Smart search filter: partial match on part_no (case insensitive)
  const sq = searchQuery.toLowerCase().trim();
  const filteredReportData = sq ? reportData.filter(r => (r.part_no || "").toLowerCase().includes(sq)) : reportData;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchReport(); }, [dateFrom, dateTo]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = {};
      // Empty `dateFrom` = "from day 1 of all history". `dateTo` empty = "till today".
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      // Reset tab caches when the window changes so other tabs don't show stale numbers
      setQualityData(null); setScrapData(null); setReworkData(null);
      setScrapRegData(null); setOpWiseData(null); setQualityChartData(null);
      const finalRes = await axios.get(`${API}/reports/final`, { params });
      setReportData(finalRes.data);
    } catch { toast.error("Failed to load report"); }
    finally { setLoading(false); }
  };

  // Lazy-load tab data only when tab is selected
  useEffect(() => {
    if (activeTab === "quality" && !qualityData) {
      axios.get(`${API}/reports/quality`).then(r => setQualityData(r.data)).catch(() => toast.error("Failed to load quality report"));
    }
    if (activeTab === "quality" && qualityView === "graphs" && !qualityChartData) {
      axios.get(`${API}/reports/quality-charts`).then(r => setQualityChartData(r.data)).catch(() => toast.error("Failed to load quality charts"));
    }
    if (activeTab === "scrap" && !scrapData) {
      axios.get(`${API}/reports/scrap`).then(r => setScrapData(r.data)).catch(() => toast.error("Failed to load scrap report"));
    }
    if (activeTab === "rework" && !reworkData) {
      axios.get(`${API}/reports/rework`).then(r => setReworkData(r.data)).catch(() => toast.error("Failed to load rework report"));
    }
    if (activeTab === "scrap_registration" && !scrapRegData) {
      axios.get(`${API}/reports/scrap-registration`).then(r => setScrapRegData(r.data)).catch(() => toast.error("Failed to load scrap registration"));
    }
    if (activeTab === "operation_wise" && !opWiseData) {
      axios.get(`${API}/reports/final/operation-wise`).then(r => setOpWiseData(r.data)).catch(() => toast.error("Failed to load operation-wise summary"));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, qualityView]);

  const handleExport = async () => {
    try {
      let params;
      if (activeTab === "value") {
        params = `report_type=value&rate_type=${valueRateType}`;
      } else if (activeTab === "quality") {
        params = "report_type=quality";
      } else if (activeTab === "scrap") {
        params = "report_type=scrap";
      } else if (activeTab === "scrap_registration") {
        params = "report_type=scrap_registration";
      } else if (activeTab === "rework") {
        params = "report_type=rework";
      } else {
        params = "report_type=production";
      }
      const res = await axios.get(`${API}/reports/export?${params}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = window.URL.createObjectURL(new Blob([res.data]));
      a.download = `final_report_${activeTab}.xlsx`; a.click();
      toast.success("Report exported");
    } catch { toast.error("Export failed"); }
  };

  const pl = (op) => op === 'dispatch' ? "Dispatched" : INSPECTED_OPS.has(op) ? "Inspected" : "Produced";
  const n = (v) => typeof v === 'number' ? parseFloat(v.toFixed(2)) : (parseFloat(v) || 0);
  const d = (v) => { const r = typeof v === 'number' ? v : parseFloat(v || 0); return r === 0 ? '-' : r.toFixed(2); };
  const ybg = (v) => (parseFloat(v) || 0) > 0 ? 'bg-[#FFFDE7]' : '';

  // Pending = Scheduled - Dispatch Produced
  const getPending = (row) => {
    const sched = row.scheduled_qty || 0;
    const dispatched = row.dispatch_produced || 0;
    return Math.max(0, sched - dispatched);
  };

  const renderProductionReport = () => (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="final-report-table">
        <thead className="bg-[#1D2951] text-white">
          <tr>
            <th className="px-3 py-2 text-left font-bold sticky left-0 bg-[#1D2951] z-10" rowSpan="2">Part No</th>
            <th className="px-2 py-2 text-center font-bold border-l border-[#2A3B73]" colSpan="2">Scheduled</th>
            <th className="px-2 py-2 text-center font-bold border-l border-[#2A3B73] text-[#FCD34D]" colSpan="2" rowSpan="2">Pending</th>
            {ALL_OPERATIONS.map(op => (
              <th key={op} className="px-1 py-2 text-center font-bold border-l border-[#2A3B73]" colSpan="5">{OPERATION_NAMES[op] || op}</th>
            ))}
          </tr>
          <tr>
            <th className="px-1 py-1 text-center font-bold border-l border-[#2A3B73] text-[11px]">Quantity</th>
            <th className="px-1 py-1 text-center font-bold text-[11px]">Val</th>
            {ALL_OPERATIONS.map(op => (
              <React.Fragment key={op}>
                <th className="px-1 py-1 text-center font-bold border-l border-[#2A3B73] text-[11px]">Open</th>
                <th className="px-1 py-1 text-center font-bold text-[11px]">Recd</th>
                <th className="px-1 py-1 text-center font-bold text-[11px]">{pl(op)}</th>
                <th className="px-1 py-1 text-center font-bold text-[#A5F3FC] text-[11px]">Balance Production</th>
                <th className="px-1 py-1 text-center font-bold text-[#FCD34D] text-[11px]" title="Balance to Issue = max(0, Scheduled − Produced)">Balance Issue</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-[#E2E8F0]">
          {filteredReportData.map((row, idx) => {
            const pending = getPending(row);
            const pRate = row.part_rate || 0;
            return (
              <tr key={idx} className="hover:bg-[#F8FAFC]">
                <td className="px-3 py-2 font-mono font-semibold text-[#1D2951] sticky left-0 bg-white z-10 border-r text-xs">{row.part_no}</td>
                <td className={`px-1 py-2 text-center font-mono text-xs border-l ${ybg(row.scheduled_qty)}`}>{d(row.scheduled_qty)}</td>
                <td className={`px-1 py-2 text-center font-mono text-xs text-[#10B981] ${ybg(row.scheduled_value)}`}>{d(row.scheduled_value)}</td>
                <td className={`px-1 py-2 text-center font-mono text-xs font-bold text-[#F59E0B] border-l ${ybg(pending)}`}>{d(pending)}</td>
                <td className={`px-1 py-2 text-center font-mono text-xs text-[#F59E0B] ${ybg(pending * pRate)}`}>{d(pending * pRate)}</td>
                {ALL_OPERATIONS.map(op => (
                  <React.Fragment key={op}>
                    <td className={`px-1 py-2 text-center font-mono text-xs border-l ${ybg(row[`${op}_opening`])}`}>{d(row[`${op}_opening`])}</td>
                    <td className={`px-1 py-2 text-center font-mono text-xs ${ybg(row[`${op}_received`])}`}>{d(row[`${op}_received`])}</td>
                    <td className={`px-1 py-2 text-center font-mono text-xs ${ybg(row[`${op}_produced`])}`}>{d(row[`${op}_produced`])}</td>
                    <td className={`px-1 py-2 text-center font-mono text-xs font-bold text-[#7C3AED] ${ybg(row[`${op}_balance`])}`}>{d(row[`${op}_balance`])}</td>
                    <td className={`px-1 py-2 text-center font-mono text-xs font-bold text-[#B45309] ${ybg(row[`${op}_balance_issue`])}`}>{d(row[`${op}_balance_issue`])}</td>
                  </React.Fragment>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      {reportData.length === 0 && <div className="text-center py-12 text-[#64748B]">No data available.</div>}
    </div>
  );

  const renderValueReport = () => {
    const usePartRate = valueRateType === "part_rate";
    return (
      <div className="space-y-3">
        <div className="flex gap-2">
          <Button size="sm" variant={usePartRate ? "default" : "outline"}
            onClick={() => setValueRateType("part_rate")}
            className={usePartRate ? "bg-[#7C3AED] text-white" : "text-[#7C3AED] border-[#7C3AED]"}
            data-testid="rate-toggle-part">By Part Rate</Button>
          <Button size="sm" variant={!usePartRate ? "default" : "outline"}
            onClick={() => setValueRateType("op_rate")}
            className={!usePartRate ? "bg-[#7C3AED] text-white" : "text-[#7C3AED] border-[#7C3AED]"}
            data-testid="rate-toggle-op">By Operation Rate</Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="value-report-table">
            <thead className="bg-[#1D2951] text-white">
              {/* Balance Value summary row */}
              <tr className="bg-[#7C3AED]">
                <th className="px-3 py-1.5 text-left font-bold sticky left-0 bg-[#7C3AED] z-10 text-[11px]">Balance Value</th>
                <th className="px-2 py-1.5 border-l border-[#9B6DFF] text-[11px]"></th>
                <th className="px-2 py-1.5 border-l border-[#9B6DFF] text-[11px]" colSpan="2"></th>
                {ALL_OPERATIONS.map(op => {
                  let balVal = 0;
                  for (const row of filteredReportData) {
                    const sched = row.scheduled_qty || 0;
                    const pRate = row.part_rate || 0;
                    const opRate = row[`${op}_op_rate`] || 0;
                    const rate = usePartRate ? pRate : opRate;
                    const prod = row[`${op}_produced`] || 0;
                    const balQty = Math.max(0, sched - prod);
                    balVal += balQty * rate;
                  }
                  return (
                    <th key={op} className="px-1 py-1.5 text-center font-bold border-l border-[#9B6DFF] text-[11px] text-[#FCD34D]" colSpan={usePartRate ? 4 : 5}>
                      {balVal === 0 ? '-' : balVal.toFixed(2)}
                    </th>
                  );
                })}
              </tr>
              <tr>
                <th className="px-3 py-2 text-left font-bold sticky left-0 bg-[#1D2951] z-10" rowSpan="2">Part No</th>
                <th className="px-2 py-2 text-center font-bold border-l border-[#2A3B73]" rowSpan="2">{usePartRate ? "Part Rate" : "Part Rate"}</th>
                <th className="px-2 py-2 text-center font-bold border-l border-[#2A3B73]" colSpan="2">Scheduled</th>
                {ALL_OPERATIONS.map(op => (
                  <th key={op} className="px-1 py-2 text-center font-bold border-l border-[#2A3B73]" colSpan={usePartRate ? 4 : 5}>{OPERATION_NAMES[op] || op}</th>
                ))}
              </tr>
              <tr>
                <th className="px-1 py-1 text-center font-bold border-l border-[#2A3B73] text-[11px]">Quantity</th>
                <th className="px-1 py-1 text-center font-bold text-[11px]">Val</th>
                {ALL_OPERATIONS.map(op => (
                  <React.Fragment key={op}>
                    {!usePartRate && <th className="px-1 py-1 text-center font-bold border-l border-[#2A3B73] text-[11px]">Operation Rate</th>}
                    {usePartRate && <th className="px-1 py-1 text-center font-bold border-l border-[#2A3B73] text-[11px]">{pl(op)} Quantity</th>}
                    {!usePartRate && <th className="px-1 py-1 text-center font-bold text-[11px]">{pl(op)} Quantity</th>}
                    <th className="px-1 py-1 text-center font-bold text-[#A5F3FC] text-[11px]">{pl(op)} Val</th>
                    <th className="px-1 py-1 text-center font-bold text-[11px]">Balance Quantity</th>
                    <th className="px-1 py-1 text-center font-bold text-[#FCD34D] text-[11px]">Balance Value</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#E2E8F0]">
              {filteredReportData.map((row, idx) => {
                const sched = row.scheduled_qty || 0;
                const pRate = row.part_rate || 0;
                return (
                  <tr key={idx} className="hover:bg-[#F8FAFC]">
                    <td className="px-3 py-2 font-mono font-semibold text-[#1D2951] sticky left-0 bg-white z-10 border-r text-xs">{row.part_no}</td>
                    <td className={`px-1 py-2 text-center font-mono text-xs border-l font-bold ${ybg(pRate)}`}>{d(pRate)}</td>
                    <td className={`px-1 py-2 text-center font-mono text-xs border-l ${ybg(sched)}`}>{d(sched)}</td>
                    <td className={`px-1 py-2 text-center font-mono text-xs text-[#10B981] ${ybg(sched * pRate)}`}>{d(sched * pRate)}</td>
                    {ALL_OPERATIONS.map(op => {
                      const opRate = row[`${op}_op_rate`] || 0;
                      const rate = usePartRate ? pRate : opRate;
                      const prod = row[`${op}_produced`] || 0;
                      const balQty = Math.max(0, sched - prod);
                      return (
                        <React.Fragment key={op}>
                          {!usePartRate && <td className={`px-1 py-2 text-center font-mono text-xs border-l ${ybg(opRate)}`}>{d(opRate)}</td>}
                          <td className={`px-1 py-2 text-center font-mono text-xs ${usePartRate ? 'border-l' : ''} ${ybg(prod)}`}>{d(prod)}</td>
                          <td className={`px-1 py-2 text-center font-mono text-xs text-[#10B981] font-bold ${ybg(prod * rate)}`}>{d(prod * rate)}</td>
                          <td className={`px-1 py-2 text-center font-mono text-xs ${ybg(balQty)}`}>{d(balQty)}</td>
                          <td className={`px-1 py-2 text-center font-mono text-xs text-[#F59E0B] font-bold ${ybg(balQty * rate)}`}>{d(balQty * rate)}</td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {reportData.length === 0 && <div className="text-center py-12 text-[#64748B]">No data available.</div>}
        </div>
      </div>
    );
  };

  const renderQualityReport = () => {
    if (!qualityData) return <div className="text-center py-12 text-[#64748B]">No quality data.</div>;
    const { records, total_rejection_cost, reason_summary } = qualityData;
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4">
          <div className="bg-[#FEF2F2] rounded-lg p-3">
            <p className="text-xs text-[#64748B]">Total Rejection Cost</p>
            <p className="text-lg font-bold text-[#EF4444]">{n(total_rejection_cost)}</p>
          </div>
          <div className="bg-[#FFFBEB] rounded-lg p-3">
            <p className="text-xs text-[#64748B]">Total Records</p>
            <p className="text-lg font-bold text-[#F59E0B]">{records.length}</p>
          </div>
          {Object.entries(reason_summary || {}).slice(0, 4).map(([reason, qty]) => (
            <div key={reason} className="bg-[#F0F9FF] rounded-lg p-3">
              <p className="text-xs text-[#64748B] truncate">{reason}</p>
              <p className="text-lg font-bold text-[#1D2951]">{d(qty)}</p>
            </div>
          ))}
        </div>

        {/* Table / Graphs Toggle */}
        <div className="flex gap-2 px-4">
          <Button size="sm" variant={qualityView === "table" ? "default" : "outline"}
            onClick={() => setQualityView("table")} data-testid="quality-table-btn"
            className={qualityView === "table" ? "bg-[#1D2951] text-white" : ""}>Table</Button>
          <Button size="sm" variant={qualityView === "graphs" ? "default" : "outline"}
            onClick={() => setQualityView("graphs")} data-testid="quality-graphs-btn"
            className={qualityView === "graphs" ? "bg-[#1D2951] text-white" : ""}>Graphs</Button>
        </div>

        {qualityView === "graphs" ? renderQualityGraphs() : renderQualityTable(records)}
      </div>
    );
  };

  const renderQualityTable = (records) => (
    <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="quality-report-table">
            <thead className="bg-[#1D2951] text-white">
              <tr>
                <th className="px-3 py-2 text-left font-bold">Date</th>
                <th className="px-3 py-2 text-left font-bold">Part No</th>
                <th className="px-3 py-2 text-center font-bold">Operation</th>
                <th className="px-3 py-2 text-center font-bold text-[#EF4444]">Rejected</th>
                <th className="px-3 py-2 text-center font-bold text-[#F59E0B]">Rework</th>
                <th className="px-3 py-2 text-left font-bold">Rej Reason</th>
                <th className="px-3 py-2 text-left font-bold">Rej Remarks</th>
                <th className="px-3 py-2 text-left font-bold">Rwk Reason</th>
                <th className="px-3 py-2 text-center font-bold">Rej Cost</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#E2E8F0]">
              {records.filter(r => !sq || (r.part_no || "").toLowerCase().includes(sq)).map((r, idx) => (
                <tr key={idx} className="hover:bg-[#F8FAFC]">
                  <td className="px-3 py-2 font-mono text-xs">{r.date}</td>
                  <td className="px-3 py-2 font-mono font-semibold text-[#1D2951] text-xs">{r.part_no}</td>
                  <td className="px-3 py-2 text-center text-xs">{OPERATION_NAMES[r.operation] || r.operation}</td>
                  <td className={`px-3 py-2 text-center font-mono text-xs text-[#EF4444] font-bold ${ybg(r.rejected_qty)}`}>{d(r.rejected_qty)}</td>
                  <td className={`px-3 py-2 text-center font-mono text-xs text-[#F59E0B] font-bold ${ybg(r.rework_qty)}`}>{d(r.rework_qty)}</td>
                  <td className="px-3 py-2 text-xs">{r.rejection_reason_type}</td>
                  <td className="px-3 py-2 text-xs max-w-[200px] truncate">{r.rejection_remarks}</td>
                  <td className="px-3 py-2 text-xs">{r.rework_reason_type}</td>
                  <td className={`px-3 py-2 text-center font-mono text-xs font-bold ${ybg(r.rejection_cost)}`}>{d(r.rejection_cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {records.length === 0 && <div className="text-center py-8 text-[#64748B]">No rejections or rework recorded.</div>}
        </div>
  );

  const renderQualityGraphs = () => {
    const cData = (qualityChartData?.chart_data || []).map(d => ({
      ...d, short_name: d.part_name?.length > 12 ? d.part_name.substring(0, 12) + '...' : d.part_name
    }));
    if (cData.length === 0) return <div className="text-center py-12 text-[#64748B]">No chart data available.</div>;

    const renderValueLabel = ({ x, y, width, value }) => {
      if (!value || value === 0 || width < 20) return null;
      return (
        <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={9} fill="#1D2951" fontWeight="bold" fontFamily="monospace">
          {typeof value === 'number' ? (value >= 1000 ? (value / 1000).toFixed(1) + 'K' : value.toFixed(1)) : value}
        </text>
      );
    };

    const renderPctLabel = ({ x, y, width, value }) => {
      if (!value || value === 0 || width < 20) return null;
      return (
        <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={9} fill="#1D2951" fontWeight="bold" fontFamily="monospace">
          {value.toFixed(1)}%
        </text>
      );
    };

    const VChart = ({ title, dataKey, color, isPct, isValue }) => (
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 shadow-sm">
        <h4 className="text-sm font-bold text-[#1D2951] mb-4" style={{ fontFamily: 'Chivo' }}>{title}</h4>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={cData} margin={{ top: 20, right: 10, left: 10, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="short_name" tick={{ fontSize: 9, fill: '#475569' }} interval={0} angle={-35} textAnchor="end" height={80} />
            <YAxis tick={{ fontSize: 10, fill: '#64748B' }} width={50}
              tickFormatter={v => isPct ? `${v}%` : isValue ? (v >= 100000 ? (v/100000).toFixed(0) + 'L' : v >= 1000 ? (v/1000).toFixed(0) + 'K' : v.toFixed(0)) : v.toFixed(0)} />
            <Tooltip formatter={(v) => [isPct ? `${v.toFixed(2)}%` : isValue ? `Rs. ${v.toFixed(2)}` : v.toFixed(2), title]}
              labelFormatter={(label) => label}
              contentStyle={{ fontSize: 12, borderRadius: 10, border: '1px solid #E2E8F0', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }} />
            <Bar dataKey={dataKey} fill={color} radius={[4, 4, 0, 0]} maxBarSize={45}
              label={isPct ? renderPctLabel : renderValueLabel} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-4">
        <VChart title="FTR Quantity in Nos" dataKey="ftr_qty" color="#7F1D1D" />
        <VChart title="Rejection Quantity" dataKey="rejection_qty" color="#991B1B" />
        <VChart title="FTR %" dataKey="ftr_pct" color="#7F1D1D" isPct />
        <VChart title="Rejection %" dataKey="rejection_pct" color="#991B1B" isPct />
        <VChart title="FTQ Value" dataKey="ftq_value" color="#7F1D1D" isValue />
        <VChart title="Rejection Value" dataKey="rejection_value" color="#991B1B" isValue />
      </div>
    );
  };

  const toggleScrap = async (transId) => {
    try {
      const res = await axios.put(`${API}/reports/scrap/${transId}/toggle`);
      // Update local state
      setScrapData(prev => ({
        ...prev,
        records: prev.records.map(r => r.id === transId ? { ...r, scrapped: res.data.scrapped } : r),
        total_scrapped_nos: prev.records.reduce((sum, r) => sum + (r.id === transId ? (res.data.scrapped ? r.rejected_qty : 0) : (r.scrapped ? r.rejected_qty : 0)), 0),
        total_scrapped_kg: prev.records.reduce((sum, r) => sum + (r.id === transId ? (res.data.scrapped ? r.rejected_kg : 0) : (r.scrapped ? r.rejected_kg : 0)), 0)
      }));
      toast.success(res.data.scrapped ? "Marked as scrapped" : "Unmarked scrap");
    } catch { toast.error("Failed to toggle scrap"); }
  };

  const renderScrapReport = () => {
    if (!scrapData) return <div className="text-center py-12 text-[#64748B]">No scrap data.</div>;
    const { records } = scrapData;
    const selected = records.filter(r => r.scrapped);
    const totalNos = selected.reduce((s, r) => s + r.rejected_qty, 0);
    const totalKg = selected.reduce((s, r) => s + r.rejected_kg, 0);

    const approveSelected = async () => {
      if (selected.length === 0) return toast.error("Select items to approve first");
      try {
        const res = await axios.post(`${API}/reports/scrap/approve`);
        toast.success(res.data.message);
        // Refresh scrap data — approved items will be removed
        const newData = await axios.get(`${API}/reports/scrap`);
        setScrapData(newData.data);
        // Also refresh scrap registration if loaded
        setScrapRegData(null);
      } catch { toast.error("Failed to approve"); }
    };

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex gap-3">
            <div className="bg-[#FEF2F2] rounded-lg p-3 min-w-[120px]">
              <p className="text-xs text-[#64748B]">Records</p>
              <p className="text-lg font-bold text-[#EF4444]">{records.length}</p>
            </div>
            <div className="bg-[#FEF2F2] rounded-lg p-3 min-w-[120px]">
              <p className="text-xs text-[#64748B]">Selected (Nos)</p>
              <p className="text-lg font-bold text-[#DC2626]">{n(totalNos)}</p>
            </div>
            <div className="bg-[#FEF2F2] rounded-lg p-3 min-w-[120px]">
              <p className="text-xs text-[#64748B]">Selected (KG)</p>
              <p className="text-lg font-bold text-[#DC2626]">{n(totalKg)}</p>
            </div>
          </div>
          {selected.length > 0 && (
            <button onClick={approveSelected} data-testid="approve-scrap-btn"
              className="bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold px-6 py-3 rounded-lg text-sm transition-colors">
              Approve {selected.length} Selected → Scrap Registration
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="scrap-report-table">
            <thead className="bg-[#1D2951] text-white">
              <tr>
                <th className="px-3 py-2 text-center font-bold">Select</th>
                <th className="px-3 py-2 text-left font-bold">Date</th>
                <th className="px-3 py-2 text-left font-bold">Part No</th>
                <th className="px-3 py-2 text-center font-bold">Operation</th>
                <th className="px-3 py-2 text-center font-bold text-[#EF4444]">Rejected Quantity</th>
                <th className="px-3 py-2 text-center font-bold">Rejected KG</th>
                <th className="px-3 py-2 text-left font-bold">Reason</th>
                <th className="px-3 py-2 text-left font-bold">Remarks</th>
                <th className="px-3 py-2 text-center font-bold">Entry ID</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#E2E8F0]">
              {records.filter(r => !sq || (r.part_no || "").toLowerCase().includes(sq)).map((r, idx) => (
                <tr key={idx} className={`hover:bg-[#F8FAFC] ${r.scrapped ? 'bg-red-50' : ''}`}>
                  <td className="px-3 py-2 text-center">
                    <input type="checkbox" checked={r.scrapped} onChange={() => toggleScrap(r.id)}
                      className="w-4 h-4 accent-[#EF4444] cursor-pointer" data-testid={`scrap-check-${idx}`} />
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{r.date}</td>
                  <td className="px-3 py-2 font-mono font-semibold text-[#1D2951] text-xs">{r.part_no}</td>
                  <td className="px-3 py-2 text-center text-xs">{r.operation_name}</td>
                  <td className={`px-3 py-2 text-center font-mono text-xs text-[#EF4444] font-bold ${ybg(r.rejected_qty)}`}>{d(r.rejected_qty)}</td>
                  <td className={`px-3 py-2 text-center font-mono text-xs ${ybg(r.rejected_kg)}`}>{d(r.rejected_kg)}</td>
                  <td className="px-3 py-2 text-xs">{r.rejection_reason}</td>
                  <td className="px-3 py-2 text-xs max-w-[200px] truncate">{r.rejection_remarks}</td>
                  <td className="px-3 py-2 text-center font-mono text-xs">{r.data_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {records.length === 0 && <div className="text-center py-8 text-[#64748B]">No rejections pending approval. All approved items are in Scrap Registration.</div>}
        </div>
      </div>
    );
  };

  const printReworkReceipt = (r) => {
    const html = `<html><head><title>Rework Receipt</title><style>
      body{font-family:Arial;padding:30px;max-width:600px;margin:auto}
      h2{text-align:center;border-bottom:2px solid #1D2951;padding-bottom:10px}
      table{width:100%;border-collapse:collapse;margin-top:20px}
      td,th{border:1px solid #ddd;padding:8px;text-align:left}
      th{background:#1D2951;color:white}
      .footer{margin-top:40px;display:flex;justify-content:space-between}
      .sign{border-top:1px solid #333;padding-top:5px;width:150px;text-align:center}
    </style></head><body>
    <h2>Rework Receipt</h2>
    <table>
      <tr><th>Date</th><td>${r.date}</td></tr>
      <tr><th>Part No</th><td>${r.part_no}</td></tr>
      <tr><th>From Operation</th><td>${r.operation_name}</td></tr>
      <tr><th>Rework To</th><td>${r.rework_to_name || '-'}</td></tr>
      <tr><th>Rework Quantity</th><td>${r.rework_qty} Nos (${d(r.rework_kg)} KG)</td></tr>
      <tr><th>Reason</th><td>${r.rework_reason || '-'}</td></tr>
      <tr><th>Remarks</th><td>${r.rework_remarks || '-'}</td></tr>
      <tr><th>Entry ID</th><td>${r.data_id}</td></tr>
    </table>
    <div class="footer">
      <div class="sign">Issued By</div>
      <div class="sign">Received By</div>
    </div>
    </body></html>`;
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    document.body.appendChild(iframe);
    iframe.contentDocument.write(html);
    iframe.contentDocument.close();
    setTimeout(() => { iframe.contentWindow.print(); setTimeout(() => document.body.removeChild(iframe), 1000); }, 500);
  };


  // ──────────────────────────── Operation-Wise Tab ─────────────────────────
  // Reads /reports/final/operation-wise. Three columns per operation:
  //  · Done Quantity (period)             — produced this month
  //  · Pending vs Schedule           — Σ (ordered − issued out of this op)
  //  · Pending vs Stock              — Σ (closing balance per part for this op)
  // Plus a value column next to each. Totals at the bottom.
  const renderOperationWise = () => {
    if (!opWiseData) return <div className="text-center py-12 text-[#64748B]">Loading operation-wise summary…</div>;
    const rows = opWiseData.rows || [];
    const t = opWiseData.totals || {};
    if (rows.length === 0) return <div className="text-center py-12 text-[#64748B]">No operation data.</div>;
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="op-wise-table">
          <thead className="bg-[#1D2951] text-white">
            <tr>
              <th className="px-3 py-2.5 text-left text-[11px] font-bold border-r border-[#334155]">Operation</th>
              <th className="px-3 py-2.5 text-right text-[11px] font-bold border-r border-[#334155]">Done Quantity</th>
              <th className="px-3 py-2.5 text-right text-[11px] font-bold border-r border-[#334155]">Done Value</th>
              <th className="px-3 py-2.5 text-right text-[11px] font-bold border-r border-[#334155]">Pending vs Scheduled Quantity</th>
              <th className="px-3 py-2.5 text-right text-[11px] font-bold border-r border-[#334155]">Pending vs Scheduled Value</th>
              <th className="px-3 py-2.5 text-right text-[11px] font-bold border-r border-[#334155]">Pending vs Stock Quantity</th>
              <th className="px-3 py-2.5 text-right text-[11px] font-bold">Pending vs Stock Value</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E2E8F0]">
            {rows.map((r, i) => (
              <tr key={r.operation} className={i % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"} data-testid={`op-wise-row-${r.operation}`}>
                <td className="px-3 py-2 font-semibold text-[#1D2951]">{OPERATION_NAMES[r.operation] || r.operation}</td>
                <td className="px-3 py-2 text-right font-mono text-[#059669] font-bold">{d(r.done_qty)}</td>
                <td className="px-3 py-2 text-right font-mono text-[#059669]">{d(r.done_value)}</td>
                <td className="px-3 py-2 text-right font-mono text-[#DC2626] font-bold">{d(r.pending_sched_qty)}</td>
                <td className="px-3 py-2 text-right font-mono text-[#DC2626]">{d(r.pending_sched_value)}</td>
                <td className="px-3 py-2 text-right font-mono text-[#7C3AED] font-bold">{d(r.pending_stock_qty)}</td>
                <td className="px-3 py-2 text-right font-mono text-[#7C3AED]">{d(r.pending_stock_value)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-[#0F172A] text-white">
              <td className="px-3 py-2 font-bold">TOTAL</td>
              <td className="px-3 py-2 text-right font-mono font-bold">{d(t.done_qty)}</td>
              <td className="px-3 py-2 text-right font-mono font-bold">{d(t.done_value)}</td>
              <td className="px-3 py-2 text-right font-mono font-bold">{d(t.pending_sched_qty)}</td>
              <td className="px-3 py-2 text-right font-mono font-bold">{d(t.pending_sched_value)}</td>
              <td className="px-3 py-2 text-right font-mono font-bold">{d(t.pending_stock_qty)}</td>
              <td className="px-3 py-2 text-right font-mono font-bold">{d(t.pending_stock_value)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  const renderReworkReport = () => {
    if (!reworkData) return <div className="text-center py-12 text-[#64748B]">No rework data.</div>;
    const { records } = reworkData;
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4">
          <div className="bg-[#FFFBEB] rounded-lg p-3">
            <p className="text-xs text-[#64748B]">Total Rework Records</p>
            <p className="text-lg font-bold text-[#F59E0B]">{records.length}</p>
          </div>
          <div className="bg-[#FFFBEB] rounded-lg p-3">
            <p className="text-xs text-[#64748B]">Total Rework Quantity (Nos)</p>
            <p className="text-lg font-bold text-[#D97706]">{d(records.reduce((s, r) => s + r.rework_qty, 0))}</p>
          </div>
          <div className="bg-[#FFFBEB] rounded-lg p-3">
            <p className="text-xs text-[#64748B]">Total Rework (KG)</p>
            <p className="text-lg font-bold text-[#D97706]">{d(records.reduce((s, r) => s + r.rework_kg, 0))}</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="rework-report-table">
            <thead className="bg-[#1D2951] text-white">
              <tr>
                <th className="px-3 py-2 text-left font-bold">Date</th>
                <th className="px-3 py-2 text-left font-bold">Part No</th>
                <th className="px-3 py-2 text-center font-bold">From Operation</th>
                <th className="px-3 py-2 text-center font-bold text-[#FCD34D]">Rework To</th>
                <th className="px-3 py-2 text-center font-bold text-[#F59E0B]">Rework Quantity</th>
                <th className="px-3 py-2 text-center font-bold">Rework KG</th>
                <th className="px-3 py-2 text-left font-bold">Reason</th>
                <th className="px-3 py-2 text-left font-bold">Remarks</th>
                <th className="px-3 py-2 text-center font-bold">Print</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#E2E8F0]">
              {records.filter(r => !sq || (r.part_no || "").toLowerCase().includes(sq)).map((r, idx) => (
                <tr key={idx} className="hover:bg-[#F8FAFC]">
                  <td className="px-3 py-2 font-mono text-xs">{r.date}</td>
                  <td className="px-3 py-2 font-mono font-semibold text-[#1D2951] text-xs">{r.part_no}</td>
                  <td className="px-3 py-2 text-center text-xs">{r.operation_name}</td>
                  <td className={`px-3 py-2 text-center text-xs font-bold text-[#F59E0B] ${ybg(r.rework_qty)}`}>{r.rework_to_name || '-'}</td>
                  <td className={`px-3 py-2 text-center font-mono text-xs text-[#F59E0B] font-bold ${ybg(r.rework_qty)}`}>{d(r.rework_qty)}</td>
                  <td className={`px-3 py-2 text-center font-mono text-xs ${ybg(r.rework_kg)}`}>{d(r.rework_kg)}</td>
                  <td className="px-3 py-2 text-xs">{r.rework_reason}</td>
                  <td className="px-3 py-2 text-xs max-w-[200px] truncate">{r.rework_remarks}</td>
                  <td className="px-3 py-2 text-center">
                    <Button variant="ghost" size="sm" onClick={() => printReworkReceipt(r)}
                      className="text-[#1D2951] h-7 px-2" data-testid={`print-rework-${idx}`}>
                      <Printer size={14} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {records.length === 0 && <div className="text-center py-8 text-[#64748B]">No rework recorded.</div>}
        </div>
      </div>
    );
  };

  const renderScrapRegistration = () => {
    if (!scrapRegData) return <div className="text-center py-8 text-[#64748B]">Loading...</div>;
    const records = scrapRegData.records || [];
    return (
      <div className="overflow-x-auto">
        <div className="mb-3 flex items-center justify-between flex-wrap gap-3">
          <h3 className="text-sm font-bold text-[#1D2951]">Scrap Registration — Approved Scrap with Rejection Value</h3>
          <div className="flex gap-4 text-xs">
            <span className="bg-[#FEF2F2] px-3 py-1 rounded font-bold text-[#EF4444]">Total Quantity: {d(scrapRegData.total_qty)}</span>
            <span className="bg-[#FEF2F2] px-3 py-1 rounded font-bold text-[#EF4444]">Total Value: {d(scrapRegData.total_value)}</span>
          </div>
        </div>
        <table className="w-full text-xs" data-testid="scrap-registration-table">
          <thead className="bg-[#7F1D1D] text-white">
            <tr>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Data ID</th>
              <th className="px-3 py-2 text-left">Part No</th>
              <th className="px-3 py-2 text-left">Operation</th>
              <th className="px-3 py-2 text-right">Rejection Quantity</th>
              <th className="px-3 py-2 text-right">Rej KG</th>
              <th className="px-3 py-2 text-left">Reason</th>
              <th className="px-3 py-2 text-right">RM Value/pc</th>
              <th className="px-3 py-2 text-right">Operation Cost/pc</th>
              <th className="px-3 py-2 text-right">Rate/pc (+5%)</th>
              <th className="px-3 py-2 text-right">Total Value</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E2E8F0]">
            {records.filter(r => !sq || (r.part_no || "").toLowerCase().includes(sq)).map((r, i) => (
              <tr key={i} className="hover:bg-red-50">
                <td className="px-3 py-2">{r.date}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.data_id}</td>
                <td className="px-3 py-2 font-mono font-bold">{r.part_no}</td>
                <td className="px-3 py-2">{r.operation_name}</td>
                <td className={`px-3 py-2 text-right font-mono font-bold text-[#EF4444] ${ybg(r.rejected_qty)}`}>{d(r.rejected_qty)}</td>
                <td className={`px-3 py-2 text-right font-mono ${ybg(r.rejected_kg)}`}>{d(r.rejected_kg)}</td>
                <td className="px-3 py-2">{r.rejection_reason || "-"}</td>
                <td className={`px-3 py-2 text-right font-mono text-[#6366F1] ${ybg(r.rm_value_per_piece)}`}>{d(r.rm_value_per_piece)}</td>
                <td className={`px-3 py-2 text-right font-mono text-[#059669] ${ybg(r.op_cost_sum)}`}>{d(r.op_cost_sum)}</td>
                <td className={`px-3 py-2 text-right font-mono ${ybg(r.rejection_rate)}`}>{d(r.rejection_rate)}</td>
                <td className={`px-3 py-2 text-right font-mono font-bold text-[#DC2626] ${ybg(r.rejection_value)}`}>{d(r.rejection_value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {records.length === 0 && <div className="text-center py-8 text-[#64748B]">No approved scrap items. Select and approve items in Scrap Report first.</div>}
        <p className="text-xs text-[#64748B] mt-2">* Rate = (RM Value + Sum of Operation Costs to rejection point) + 5%. Total Value = Rate x Quantity.</p>
      </div>
    );
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-full">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[#1D2951]" style={{fontFamily:'Chivo'}}>Final Report</h1>
            <p className="text-sm text-[#64748B]">Production summary across all operations</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Set Duration — leave Date From blank to mean "from day 1 of all history" */}
            <div className="flex items-end gap-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-2 py-1.5">
              <div className="flex flex-col">
                <span className="text-[9px] uppercase tracking-wide text-[#64748B] font-bold">From</span>
                <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                  className="h-8 w-[140px] text-xs border-0 bg-transparent p-1"
                  placeholder="Day 1"
                  data-testid="final-from-date"
                  title="Leave blank to include everything from day 1" />
              </div>
              <span className="text-[#94A3B8] text-xs pb-1.5">→</span>
              <div className="flex flex-col">
                <span className="text-[9px] uppercase tracking-wide text-[#64748B] font-bold">To</span>
                <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                  className="h-8 w-[140px] text-xs border-0 bg-transparent p-1"
                  data-testid="final-to-date" />
              </div>
              {(dateFrom || dateTo) && (
                <Button size="sm" variant="ghost"
                  onClick={() => { setDateFrom(""); setDateTo(""); }}
                  className="h-8 px-2 text-[10px] text-[#64748B]"
                  data-testid="final-clear-duration"
                  title="Clear duration → all data">Clear</Button>
              )}
            </div>
            <Button onClick={handleExport} className="bg-[#1D2951] hover:bg-[#2A3B73] text-white min-h-[40px]" data-testid="export-report-btn">
              <DownloadSimple size={16} className="mr-1" /> Export Excel
            </Button>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap items-center">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <Input placeholder="Search Part No..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              className="pl-9 min-h-[40px] w-[220px] text-sm" data-testid="final-report-search" />
          </div>
          {[
            { key: "production", label: "Production Report" },
            { key: "value", label: "Value Report" },
            { key: "operation_wise", label: "Operation-wise" },
            { key: "quality", label: "Quality Report" },
            { key: "scrap", label: "Scrap Report" },
            { key: "scrap_registration", label: "Scrap Registration" },
            { key: "rework", label: "Rework Report" }
          ].map(t => (
            <Button key={t.key} variant={activeTab === t.key ? "default" : "outline"} size="sm"
              onClick={() => setActiveTab(t.key)}
              className={activeTab === t.key ? "bg-[#1D2951] text-white" : "text-[#1D2951]"}
              data-testid={`report-tab-${t.key}`}>
              {t.label}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12 text-[#64748B]">Loading report data...</div>
        ) : (
          <Card className="border border-[#E2E8F0]">
            {activeTab === "production" && renderProductionReport()}
            {activeTab === "value" && renderValueReport()}
            {activeTab === "operation_wise" && renderOperationWise()}
            {activeTab === "quality" && renderQualityReport()}
            {activeTab === "scrap" && renderScrapReport()}
            {activeTab === "scrap_registration" && renderScrapRegistration()}
            {activeTab === "rework" && renderReworkReport()}
          </Card>
        )}
      </div>
    </Layout>
  );
}
